import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';
import { toast } from 'react-toastify';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import ClearIcon from '@mui/icons-material/Clear';
import PublishIcon from '@mui/icons-material/Publish';
import GetAppIcon from '@mui/icons-material/GetApp';

import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';

import PopupMenu from '../popupmenu';
import ConfirmationDialog from '../confirmationdlg';
import FilterBuilder, { ToFilter, EMPTY_FILTER } from './filterbuilder';

// import { useWhatChangedEffect } from '../../libraries/withwhyrender';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '2px 4px',
        display: 'flex',
        alignItems: 'center',
        minWidth: 295,
    },
    input: {
        paddingLeft: theme.spacing(1),
        flex: 1,
    },
    iconButton: {
        padding: 10,
    },
    divider: {
        height: 28,
        margin: 4,
    },
    uploadBtn: {
        width: 25,
        minWidth: 25,
    },
}));

const FilterSearchCombo = ({
    filter: filterProp = EMPTY_FILTER,
    search: searchProp = '',
    show: showProp = false,
    searchPlaceholder = 'search',
    ...props
}) => {
    const classes = useStyles();
    const [filter, setFilter] = useState();
    const [show, setShow] = useState(false);
    const [search, setSearch] = useState('');
    const [modified, setModified] = useState(false);
    const [savedFilterSelection, setSavedFilterSelection] = useState();
    const [deleteSavedFilter, setDeleteSavedFilter] = useState();
    const [getSavedFilterName, setGetSavedFilterName] = useState();

    const handleOnShow = () => {
        props.onShow(!show);
    };

    const handleSearchChange = event => {
        setSearch(event.target.value);
    };

    const handleClearSearch = () => {
        setSearch('');
        props.onChange({ search: '' });
    };

    // actionable handlers
    const handleClearAll = () => {
        setSavedFilterSelection();
        props.onSelectSavedFilter();
        props.onShow(false);
    };

    const handleOnFilter = ({ filter }) => {
        props.onChange({ filter });
    };

    const handleOnSearch = e => {
        e.preventDefault();
        props.onChange({ search });
    };

    const handleOnSelectSavedFilter = f => {
        if (f && savedFilterSelection && (f.uuid === savedFilterSelection.uuid)) {
            // deselect but don't refresh the page
            setSavedFilterSelection();
            return;
        }
        setSavedFilterSelection(f);
        props.onSelectSavedFilter(f);
    };

    const handleOnDeleteSavedFilter = () => {
        props.onDeleteSavedFilter(deleteSavedFilter);
        setDeleteSavedFilter(undefined);
    };

    const handleAddNewFilter = () => {
        setGetSavedFilterName(true);
    };

    const handleOnSaveFilter = () => {
        if (savedFilterSelection) {
            const f = {
                ...savedFilterSelection,
                data: {
                    ...savedFilterSelection.data,
                    filter: ToFilter(filter),
                    search
                }
            };
            props.onUpdateSavedFilter(f);
        }
    };

    const handleOnNewFilter = name => {
        const f = { name, data: { filter: ToFilter(filter), search } };
        setSavedFilterSelection(f);
        props.onNewSavedFilter(f);
        setGetSavedFilterName(false);
    };

    const handleOnFilterExport = e => {
        props.onFilterExport({ filter: ToFilter(filter), search });
    };

    const handleOnImport = e => {
        const fileHandler = (file) => {
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
                reader.onload = event => resolve(event.target.result);
                reader.onerror = error => reject(error);
                reader.readAsText(file);
            });
        };
        fileHandler(e.target.files[0]).then(content => {
            try {
                const data = JSON.parse(content);
                setSavedFilterSelection();
                props.onFilterImport(data);
            } catch (err) {
                toast.error('Import file is not a valid filter file');
                console.error(err);
            }
        }).catch(err => {
            toast.error('Cannot import this file file');
            console.error(err);
        });
    };

    // modified hook
    useEffect(() => {
        setModified(searchProp !== search);
    }, [searchProp, search]);

    useEffect(() => {
        // update current selection if it's still in the list (i.e removed by someone else)
        setSavedFilterSelection(current => {
            if (current) {
                return (props.savedFilters || []).find(f => f.name === current.name);
            } else return current;
        });
    }, [props.savedFilters]);

    // incoming search and filter
    useEffect(() => {
        setFilter(filterProp);
        setSearch(searchProp || '');
    }, [filterProp, searchProp]);

    // incoming open/close
    useEffect(() => {
        setShow(showProp);
    }, [showProp]);

    // console.log("fields", props.fields, "show", show, "showtimeout", props.showTimeout, "filter", filter)

    return (
        <Paper component="form" className={classes.root} name="filter_form">
            {/* saved searches */}
            <PopupMenu
                tooltip="save filter"
                items={props.savedFilters || []}
                itemkey="name"
                onSelect={handleOnSelectSavedFilter}
                icon={<SaveIcon />}
                placement={'bottom-start'}
                altaction={props.onDeleteSavedFilter ? setDeleteSavedFilter : undefined}
                actionicon={<DeleteIcon color="secondary" fontSize="small" />}
                selection={savedFilterSelection ? savedFilterSelection.name : undefined}
                moreActions={[
                    {
                        icon: <AddIcon fontSize="small" color="primary" />,
                        title: 'Add New Saved Filter',
                        key: 'new',
                        callback: props.onNewSavedFilter ? handleAddNewFilter : undefined,
                        divider: true,
                    },
                    {
                        icon: <SaveIcon fontSize="small" color="primary" />,
                        title: savedFilterSelection
                            ? `Update ${savedFilterSelection.name}` : undefined,
                        key: 'update',
                        callback: savedFilterSelection && props.onUpdateSavedFilter
                            ? handleOnSaveFilter : undefined,
                        divider: true,
                    },
                    {
                        icon: <GetAppIcon fontSize="small" color="primary" />,
                        title: 'Export Filter',
                        key: 'export',
                        callback: handleOnFilterExport,
                    },
                    {
                        icon: <label htmlFor="filter-import-file">
                            <Button component="div" className={classes.uploadBtn}>
                                <PublishIcon fontSize="small" color="primary" />
                            </Button>
                            Import Filter
                        </label>,
                        title: '',
                        key: 'import',
                        callback: () => { },
                        clickable: false,
                        divider: true,
                    },
                ]} />
            <input
                id="filter-import-file"
                style={{ display: 'none' }}
                aria-label="handle import file"
                type="file"
                onClick={(e) => { e.target.value = null }}
                onChange={handleOnImport} />

            {/* filter builder if filter supplied */}
            <React.Fragment>
                <FilterBuilder
                    fields={props.fields}
                    show={show}
                    showTimeout={props.showTimeout}
                    filter={filter}
                    onShow={handleOnShow}
                    onOK={handleOnFilter}
                    placement="bottom-start" />
            </React.Fragment>

            {/* text search input */}
            <InputBase
                className={classes.input}
                value={search}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                inputProps={{ 'aria-label': searchPlaceholder, spellCheck: false }} />

            {/* clear search button */}
            <IconButton
                title="clear"
                className={classes.iconButton}
                color="default"
                disabled={!search}
                aria-label="clear"
                onClick={handleClearSearch}
                size="large">
                <ClearIcon />
            </IconButton>

            {/* apply button */}
            <IconButton
                title="search"
                className={classes.iconButton}
                type="submit"
                aria-label="search"
                disabled={!modified}
                onClick={handleOnSearch}
                size="large">
                <SearchIcon />
            </IconButton>

            {/* clear button */}
            <Divider className={classes.divider} orientation="vertical" />
            <IconButton
                title="reset page"
                className={classes.iconButton}
                color="default"
                aria-label="clear"
                onClick={handleClearAll}
                size="large">
                <SettingsBackupRestoreIcon />
            </IconButton>

            {/* delete confirmation dlg */}
            {deleteSavedFilter &&
                <ConfirmationDialog
                    onClose={() => setDeleteSavedFilter(undefined)}
                    onOK={handleOnDeleteSavedFilter}
                    open={true}
                    message={`Delete saved filter '${deleteSavedFilter.name}'?`} />}

            {/* get new saved filter name */}
            {getSavedFilterName &&
                <FilterNameDialog
                    onClose={() => setGetSavedFilterName(undefined)}
                    onOK={handleOnNewFilter}
                    open={true}
                    existing={(props.savedFilters || []).map(f => f.name)} />}

        </Paper>
    );
};

FilterSearchCombo.propTypes = {
    fields: PropTypes.array.isRequired,
    show: PropTypes.bool,
    showTimeout: PropTypes.shape({
        exit: PropTypes.number.isRequired
    }),
    filter: PropTypes.object,
    search: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    onShow: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,

    savedFilters: PropTypes.array,
    onSelectSavedFilter: PropTypes.func.isRequired,
    onDeleteSavedFilter: PropTypes.func,
    onUpdateSavedFilter: PropTypes.func,
    onNewSavedFilter: PropTypes.func,
    onFilterExport: PropTypes.func.isRequired,
    onFilterImport: PropTypes.func.isRequired,
};

function FilterNameDialog({ existing = [], ...props }) {
    const [name, setName] = React.useState('');
    const { onClose, onOK, open, ...rest } = props;

    const handleChange = (event) => {
        setName(event.target.value);
    };

    const duplicate = () => {
        return (existing || []).find(e => e === name) !== undefined;
    };

    // todo: add regex validation
    const badName = () => {
        return !name || name.length < 1;
    };

    const valid = () => {
        return !duplicate() && !badName();
    };

    const handleCloseBackdrop = (e, reason) => {
        console.log('reason', reason);
    };

    return (
        <Dialog
            onClose={handleCloseBackdrop}
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="New Saved Filter"
            open={open}
            {...rest}>
            <DialogTitle id="confirmation-dialog-title" >
                New Saved Filter
            </DialogTitle>
            <DialogContent dividers>
                <form noValidate autoComplete="off">
                    <TextField
                        style={{ minWidth: 250 }}
                        error={!valid()}
                        label="Name"
                        value={name}
                        onChange={handleChange}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={props.onClose} color="primary" aria-label="cancel">
                    Cancel
                </Button>
                <Button onClick={() => props.onOK(name)} color="primary" disabled={!valid()} aria-label="save">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

FilterNameDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    onOK: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    existing: PropTypes.array,
};

export default memo(FilterSearchCombo);
